import { ChangeDetectorRef, Component, DebugElement, OnDestroy, OnInit, Inject } from '@angular/core';
import { Location, DOCUMENT } from '@angular/common';

import { PrimeNGConfig } from 'primeng/api';
import { environment } from '../environments/environment';

import { Router, NavigationEnd } from '@angular/router';
import { BehaviorSubject, filter, Observable, Subject, Subscription, takeUntil, tap } from 'rxjs';
import { ApiService } from './services/api.service';
import { AuthService } from './services/auth.service';

import { ModulesResponse } from './models/modules-response-dto';
import {
  AccountInfo,
  EventType,
  AuthenticationResult,
  EventMessage,
  InteractionStatus,
} from '@azure/msal-browser';
import { RoutingService } from './services/routing.service';
import { PAGES, INFO_PAGES } from './constants';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { SearchService } from './services/search.service';
import { WindowOpennerService } from './services/window-openner.service';
import { FieldsListsService } from './services/fields-lists.service';
import { MSALInstanceService } from './services/msal-instance.service';
import { MsalBroadcastService } from '@azure/msal-angular';
import { LanguageService } from './services/language.service';
import { LanguageConfigDTO } from './models/language-config-dto';
import { ModuleHeaderData } from './models/module-header-data';
import { PageItem,UnifiedModuleDto,UserMenuItem } from './components/module-header/header-interfaces';
import { ArcgisAuthService } from './services/arcgis-auth.service';


@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly _destroying$ = new Subject<void>();
  authResult = '';
  userName = '';
  loginDisplay = false;
  roles: string[] = [];
  private subscription: Subscription = new Subscription();
  autoLoginAttempted = false;
  private destroying$ = new Subject<void>();
  isLoading = true;
  isAuthenticated = false;
  isAnonymous = false
  errorMessage = '';
  accountInfo: AccountInfo | null = null;
  title = 'unified-mvp-frontend';
  location!: Location;
  activePage: PageItem = PAGES[0];
  modules: UnifiedModuleDto[] = [];
  pages = PAGES;
  infoPages = INFO_PAGES;
  currentLanguage$!: Observable<string>;
  languageConfig$!: Observable<LanguageConfigDTO>;
  internalUser$ = this.authService.internalUser$;
  internalUser: boolean = false;
  currentLanguage: string = '';
  currentLanguageSubscription!: Subscription;
  changeLanguageSubscription!: Subscription;
  changeLanguage:boolean=false;
  infoPagesForNonAuthenticated = INFO_PAGES.filter(page => page.id !== 2 && page.id !== 6);
  moduleHeaderData: ModuleHeaderData | undefined;
  isAiSearchPage = false;
  userMenuItems: UserMenuItem[] = [
    {
      label: '',
      icon: 'pi pi-sign-out',
      command: this.logout.bind(this),
      iconStyle: '',
    },
  ];


  setActivePage(id: number): void {
    this.activePage.id = id;
  }

  constructor(
    private routingService: RoutingService,
    private authService: AuthService,
    private primengConfig: PrimeNGConfig,
    private searchService: SearchService,
    private opener: WindowOpennerService,
    public router: Router,
    public apiService: ApiService,
    public fieldsListsService: FieldsListsService,
    private msalBroadcastService: MsalBroadcastService,
    private msalInstanceService: MSALInstanceService,
    private languageService: LanguageService,
    private translate: TranslateService,
    private arcgisAuthService: ArcgisAuthService,
    private cdr: ChangeDetectorRef,
    @Inject(DOCUMENT) private document: Document,
  ) {
  }


  ngOnInit() {
    this.primeNGConfig();
    this.currentLanguage$ = this.languageService.currentLanguage$;

    const initialLanguage = this.languageService.currentLanguage.getValue();
    this.setHtmlLangAttribute(initialLanguage);

    // Track AI search page navigation
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      takeUntil(this.destroying$)
    ).subscribe(() => {
      this.isAiSearchPage = this.router.url.startsWith('/ai');
      this.cdr.detectChanges();
    });

    this.internalUser$.pipe(takeUntil(this.destroying$)).subscribe((isInternal) => {
      this.internalUser = isInternal;
      this.updateUserMenuItems();
    });

    this.currentLanguageSubscription = this.languageService.currentLanguage.subscribe((language) => {
      this.currentLanguage = language;
      this.setHtmlLangAttribute(language);

      this.initTextPages();

      this.updateModuleHeaderData(language);
      this.updateUserMenuItems();
      this.updateTabMenuItems(language);
      this.updateGuestUsername(language);
    });

    this.changeLanguageSubscription=this.languageService.changeLanguageSubject.subscribe((res) => {
      if (this.moduleHeaderData) {
        this.moduleHeaderData = {
          ...this.moduleHeaderData,
          disabledLanguageButton: res,
        };
      }
    });
    this.authService.isAnonymous$.subscribe((isAnonymous) => {
      this.isAnonymous = isAnonymous;
    });
    this.languageConfig$ = this.apiService.getLanguageConfig();
    this.authService.isAuthenticated$
      .pipe(takeUntil(this.destroying$))
      .subscribe((isAuth) => {
        this.isAuthenticated = isAuth;
        if (isAuth) {
          const account = this.authService.getUser();
          const tenant_id = account?.tenantId || '';
          if (this.authService.isGuestMode()) {
            this.authService.setInternalUser(false);
            this.authResult = 'Guest User';
            this.translate.get('iaa.moduleHeader.guest').subscribe((translatedGuest: string) => {
              this.userName = translatedGuest;
            });
            this.accountInfo = account;
            this.cdr.detectChanges();
          } else if (tenant_id === (window as any)?.sso?.entraId.tenantId) {
            this.authService.setInternalUser(true);
            this.connectArcgisPortal();
            this.authResult = JSON.stringify(account, null, 4);
            this.userName = account?.name || '';
            this.accountInfo = account;
          } else {
            this.authService.setInternalUser(false);
            this.authResult = JSON.stringify(account, null, 4);
            this.userName = account?.name || '';
            this.accountInfo = account;
          }
        } else {
          this.userName = '';
        }
      });
    this.msalBroadcastService.msalSubject$
      .pipe(takeUntil(this.destroying$))
      .subscribe((event) => {
        if (event.eventType === EventType.LOGIN_SUCCESS) {
          const authResult = event.payload as AuthenticationResult;
          if (this.msalInstanceService.msalInstance) {
            this.msalInstanceService.msalInstance.setActiveAccount(
              authResult.account
            );
          }
        }
      });
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.ACCOUNT_ADDED ||
            msg.eventType === EventType.ACCOUNT_REMOVED
        )
      )
      .subscribe((result: EventMessage) => {
        if (
          this.msalInstanceService.msalInstance &&
          this.msalInstanceService.msalInstance.getAllAccounts().length === 0
        ) {
          window.location.pathname = '/';
        } else {
        }
      });

    this.msalBroadcastService.inProgress$
      .pipe(
        filter(
          (status: InteractionStatus) => status === InteractionStatus.None
        ),
        takeUntil(this._destroying$)
      )
      .subscribe(() => { });
    this.authService.autoLoginAttempted$
      .pipe(takeUntil(this.destroying$))
      .subscribe((attempted) => {
        this.autoLoginAttempted = attempted;
      });

    this.loadModules(this.languageService.currentLanguage.getValue());

    if (true && environment.production) {
      // todo: remove true
      if (location.protocol === 'http') {
        window.location.href = location.href.replace('http', 'https');
      }
    }

    this.routingService.activePage$.subscribe((page: PageItem) => {
      this.activePage = page;
    });
  }
  decodeJwt(token: string): any {
    try {
      const parts = token.split('.');
      const decoded = atob(parts[1]); // Decode the payload part
      return JSON.parse(decoded); // Parse as JSON
    } catch (e) {
      console.error('Error decoding JWT:', e);
      return null;
    }
  }
  private primeNGConfig() {
    this.primengConfig.ripple = true;
    this.primengConfig.zIndex = {
      modal: 1300, // dialog, sidebar
      overlay: 1300, // dropdown, overlaypanel
      menu: 1000, // overlay menus
      tooltip: 1100, // tooltip
    };
    this.primeNGTranslate();
  }

  private primeNGTranslate() {
    this.languageService.initializationLanguage();
    this.subscription.add(
      this.languageService.getLanguageKeys().pipe(
        tap((res) => {
          this.primengConfig.setTranslation(res);
        })
      ).subscribe()
    );

  }

  updateUserMenuItems(): void {
    const key = this.internalUser ? 'iaa.moduleHeader.signOut' : 'iaa.moduleHeader.exit';
    this.translate.get(key).subscribe((translatedLabel: string) => {
      this.userMenuItems = this.userMenuItems.map((item) => ({
        ...item,
        label: translatedLabel,
        iconStyle: this.currentLanguage === 'en' ? { transform: 'scale(-1, 1)' } : {}
      }));
    });
  }

  updateGuestUsername(language: string): void {
    if (this.authService.isGuestMode() && this.isAuthenticated) {
      this.translate.get('iaa.moduleHeader.guest').subscribe((translatedGuest: string) => {
        this.userName = translatedGuest;
      });
    }
  }


  updateModuleHeaderData(language: string): void {
    this.translate.get([
      'iaa.moduleHeader.logoIaa',
      'iaa.moduleHeader.iaaLogoHomePage',
      'iaa.moduleHeader.archaeologyDatabase',
      'iaa.moduleHeader.iaa',
      'iaa.moduleHeader.contactUs',
      'iaa.moduleHeader.clickToContact',
      'iaa.moduleHeader.mainMenu',
      'iaa.moduleHeader.openInformationMenu',
      'iaa.moduleHeader.closeInformationMenu',
      'iaa.moduleHeader.toThePreviousPage',
      'iaa.moduleHeader.hello',
      'iaa.moduleHeader.signIn',
      'iaa.moduleHeader.informationMenu',
      'iaa.moduleHeader.changeLanguage',
      'iaa.moduleHeader.changeLanguageTooltip',
      'iaa.moduleHeader.environment',
      'iaa.moduleHeader.tutorialVideos',
      'iaa.moduleHeader.models',
      'iaa.moduleHeader.excludedLicenses',
      'iaa.moduleHeader.opensInNewTab',
      'iaa.moduleHeader.globeIcon',
      'iaa.moduleHeader.hebrew',
      'iaa.moduleHeader.english',
      'iaa.moduleHeader.userMenu',
      'iaa.moduleHeader.userIcon'
    ]).subscribe((translations: any) => {
      this.moduleHeaderData = {
        ...this.moduleHeaderData,
        ableTranslateBtn: true,
        disabledLanguageButton:this.changeLanguage,
        isEnglish: language !== 'he',
        logoIaa: translations['iaa.moduleHeader.logoIaa'],
        iaaLogoHomePage: translations['iaa.moduleHeader.iaaLogoHomePage'],
        archaeologyDatabase: translations['iaa.moduleHeader.archaeologyDatabase'],
        iaa: translations['iaa.moduleHeader.iaa'],
        contactUs: translations['iaa.moduleHeader.contactUs'],
        clickToContact: translations['iaa.moduleHeader.clickToContact'],
        mainMenu: translations['iaa.moduleHeader.mainMenu'],
        openInformationMenu: translations['iaa.moduleHeader.openInformationMenu'],
        closeInformationMenu: translations['iaa.moduleHeader.closeInformationMenu'],
        toThePreviousPage: translations['iaa.moduleHeader.toThePreviousPage'],
        hello: translations['iaa.moduleHeader.hello'],
        signIn: translations['iaa.moduleHeader.signIn'],
        informationMenu: translations['iaa.moduleHeader.informationMenu'],
        changeLanguage: translations['iaa.moduleHeader.changeLanguage'],
        changeLanguageTooltip: translations['iaa.moduleHeader.changeLanguageTooltip'],
        environment: translations['iaa.moduleHeader.environment'],
        tutorialVideos: translations['iaa.moduleHeader.tutorialVideos'],
        models: translations['iaa.moduleHeader.models'],
        excludedLicenses: translations['iaa.moduleHeader.excludedLicenses'],
        opensInNewTab: translations['iaa.moduleHeader.opensInNewTab'],
        globeIcon: translations['iaa.moduleHeader.globeIcon'],
        hebrew: translations['iaa.moduleHeader.hebrew'],
        english: translations['iaa.moduleHeader.english'],
        userMenu: translations['iaa.moduleHeader.userMenu'],
        userIcon: translations['iaa.moduleHeader.userIcon']
      };
    });
  }

  updateTabMenuItems(language: string) {
    this.modules = this.modules?.map((module) => {
      const moduleDetail = this.modules?.find(detail => detail.name === module.name);
      if (moduleDetail) {
        return {
          ...module,
          title: language === 'he'
            ? moduleDetail.multiLanguageTitle?.hebrew
            : moduleDetail.multiLanguageTitle?.english
        };
      }
      return module;
    });
    this.modules = [...this.modules];

  }

  loadModules(language: string) {
    this.apiService.getModuleDetails().subscribe((response: ModulesResponse) => {
      this.modules = response.modules.map(module => ({
        ...module,
        isActive: module.name === 'unified',
        title: language === 'he' ? module.multiLanguageTitle?.hebrew : module.multiLanguageTitle?.english
      }));
      this.modules = [...this.modules];
    });
}


  initTextPages() {

    //get ttranslations for both text and ariaLabelText

    [...this.pages, ...this.infoPages].forEach((page) => {
      this.translate.get(page.sourceText).subscribe((translatedText: string) => {
        page.text = translatedText;
      });
      if (page.sourceAriaLabelText) {
        this.translate.get(page.sourceAriaLabelText).subscribe((translatedAriaLabelText: string) => {
          page.ariaLabelText = translatedAriaLabelText;
        });
      }
    });
    // [...this.pages, ...this.infoPages].forEach((page) => {
    //   this.translate.get(page.sourceText).subscribe((translatedText: string) => {
    //     page.text = translatedText;
    //   });
    // });
  }



  onPageChange(pageId: any) {
    const index = pageId;
    const extLink = this.getExternalLink(index);
    if (extLink === '') {
      const allPages = [...this.pages, ...this.infoPages];
      const selectedPage =
        allPages.find((page: PageItem) => page.id === index) || this.pages[0];
      this.activePage = selectedPage;
      this.routingService.updateSelectedPage(selectedPage);
    } else if (index === 6) {
      this.downloadFile('software_accreditation.pdf', extLink);
    } else {
      this.opener.openUrlInNewTab(extLink);
    }
  }

  private downloadFile(filename: string, url: string) {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  private getExternalLink(index: number): string {
    const currentLanguage = this.languageService.currentLanguage.getValue();

    switch (index) {
      case 2:
        return `${window.location.origin}/?pageId=2`;
      case 5:
        return 'https://www.iaa.org.il/page/privacy';
      case 4:
        return currentLanguage === 'he'
          ? 'https://www.iaa.org.il/page/negishuttyuta'
          : 'https://www.iaa.org.il/en/page/negishuttyuta';
      case 1:
        return 'https://www.iaa.org.il/page/usage';
      case 6:
        return './assets/docs/software.pdf';
    }
    return '';
  }

  goBack() {
    this.location.back();
  }

  onLogoClick() {
    this.searchService.updateIsFirstResReturned(false);
    this.router.navigate(['/']);
  }

  logout(): void {
    const isGuest = this.authService.isGuestMode();
    if (isGuest) {
      this.authService.logout();
      this.isAuthenticated = false;
      this.userName = '';
      this.router.navigate(['/']);
    } else {
      this.msalInstanceService.logout();
      this.isAuthenticated = false;
      this.userName = '';
    }
  }

  onLangChange() {
    this.languageService.changeLanguage();
  }

  /**
   * Sets the HTML lang attribute for accessibility compliance
   * @param language - The current language code ('he' or 'en')
   */
  private setHtmlLangAttribute(language: string): void {
    try {
      // Set the lang attribute on the html element for accessibility
      this.document.documentElement.lang = language;

      if (language === 'he') {
        this.document.documentElement.dir = 'rtl';
      } else {
        this.document.documentElement.dir = 'ltr';
      }

      // Update the page title according to the selected language
      this.updatePageTitle(language);
    } catch (error) {
      console.error('Error setting HTML lang attribute:', error);
    }
  }

  /**
   * Updates the page title based on the current language
   * @param language - The current language code ('he' or 'en')
   */
  private updatePageTitle(language: string): void {
    try {
      // Use translation service to get the page title
      this.translate.get('iaa.moduleHeader.iaaTitle').subscribe((translatedTitle: string) => {
        if (translatedTitle && translatedTitle !== 'iaa.moduleHeader.iaaTitle') {
          this.document.title = translatedTitle;
        } else {
          // Fallback to hardcoded titles if translation is not available
          if (language === 'he') {
            this.document.title = 'המאגר הלאומי לארכיאולוגיה - רשות העתיקות';
          } else {
            this.document.title = 'National Archaeological Database - Israel Antiquities Authority';
          }
        }
      });
    } catch (error) {
      console.error('Error updating page title:', error);
    }
  }

  ngOnDestroy() {
    this.destroying$.next(undefined);
    this.destroying$.complete();
    this.subscription.unsubscribe();
    this.currentLanguageSubscription.unsubscribe();
  }

  	private connectArcgisPortal() {
		this.arcgisAuthService.singIn();
	}
}





